'use client';

import { useState, Suspense } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import useFeatures from '@alltrails/features/hooks/useFeatures';
import { CommunityRating } from '@alltrails/reviews/types/CommunityRating';
import Link from '@alltrails/denali/components/Link';
import Toast from '@alltrails/denali/components/Toast';
import CheckCircleFilled from '@alltrails/denali/icons/CheckCircleFilled';
import DifficultyEasy from '@alltrails/denali/icons/DifficultyEasy';
import DifficultyModerate from '@alltrails/denali/icons/DifficultyModerate';
import DifficultyHard from '@alltrails/denali/icons/DifficultyHard';
import DifficultyStrenuous from '@alltrails/denali/icons/DifficultyStrenuous';
import useDifficultyText from '@alltrails/shared/hooks/useDifficultyText';
import IconButton from '@alltrails/denali/components/IconButton';
import EditIcon from '@alltrails/denali/icons/Edit';
import ErrorIcon from '@alltrails/denali/icons/Error';
import logTrailDetailsDifficultyTapped from '@alltrails/amplitude/events/logTrailDetailsDifficultyTapped';
import ReviewChip from '@alltrails/amplitude/enums/ReviewChip';
import styles from './Difficulty.module.scss';
import useTrailEditToolbar from '@/hooks/useTrailEditToolbar';
import getDifficultyLevel from '@/utils/difficulty';

const DifficultyModal = dynamic(() => import('./DifficultyModal'), { ssr: false });

type DifficultyProps = {
  difficulty: number;
  isAdminOrPLPUser?: boolean;
  communityRating?: CommunityRating;
  trailId: number;
};

const Difficulty = ({ difficulty, isAdminOrPLPUser, communityRating, trailId }: DifficultyProps) => {
  const showStrenuousDifficultyLevel = useFeatures()?.includes('show_strenuous_difficulty_level');
  const difficultyText = useDifficultyText(difficulty, showStrenuousDifficultyLevel);
  const [isDifficultyModalOpen, setIsDifficultyModalOpen] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const intl = useIntl();
  const { openTrailEditToolbar } = useTrailEditToolbar();
  const difficultyLevel = getDifficultyLevel(difficulty);

  const handleSubmitSuccess = () => {
    setShowSuccessToast(true);
    setTimeout(() => {
      setShowSuccessToast(false);
    }, 3000);
  };

  const handleSubmitError = () => {
    setShowErrorToast(true);
    setTimeout(() => {
      setShowErrorToast(false);
    }, 3000);
  };

  const difficultyIcon = () => {
    if (difficultyLevel === 'easy') {
      return <DifficultyEasy size="xs" color="--color-difficulty-easy" />;
    }
    if (difficultyLevel === 'moderate') {
      return <DifficultyModerate size="xs" color="--color-difficulty-moderate" />;
    }
    if (difficultyLevel === 'hard') {
      return <DifficultyHard size="xs" color="--color-difficulty-hard" />;
    }
    if (difficultyLevel === 'strenuous') {
      return <DifficultyStrenuous size="xs" color="--color-difficulty-strenuous" />;
    }
    return null;
  };

  return (
    <>
      <span data-testid="trail-difficulty" className={classNames({ [styles.adminDifficulty]: isAdminOrPLPUser })}>
        {showStrenuousDifficultyLevel ? (
          <div className={styles.difficultyContainerWithIcon}>
            <div className={styles.difficultyIcon}>{difficultyIcon()}</div>
            <Link
              className={styles.difficultyLink}
              onClick={() => {
                if (difficultyLevel) {
                  logTrailDetailsDifficultyTapped({
                    trail_difficulty: difficultyLevel as ReviewChip,
                    trail_id: trailId
                  });
                }
                setIsDifficultyModalOpen(true);
              }}
              variant="primary"
              testId="difficulty-level-button"
              title={difficultyText}
            >
              {difficultyText}
            </Link>
          </div>
        ) : (
          difficultyText
        )}
        {isAdminOrPLPUser && (
          <IconButton
            icon={{ Component: EditIcon }}
            className={styles.editIcon}
            testId="edit-icon"
            variant="primary"
            onClick={() => openTrailEditToolbar()}
            size="sm"
            title={intl.formatMessage({ defaultMessage: 'Edit' })}
          />
        )}
      </span>
      {isDifficultyModalOpen && (
        <Suspense fallback={null}>
          <DifficultyModal
            isOpen={isDifficultyModalOpen}
            onClose={() => setIsDifficultyModalOpen(false)}
            difficulty={difficulty}
            communityRating={communityRating}
            trailId={trailId}
            onSubmitSuccess={handleSubmitSuccess}
            onSubmitError={handleSubmitError}
          />
        </Suspense>
      )}
      <Toast
        isOpen={showSuccessToast}
        icon={{ Component: CheckCircleFilled }}
        message={<FormattedMessage defaultMessage="Thanks for your feedback!" />}
        position="alternative"
        type="success"
        testId="difficulty-feedback-toast"
        className={styles.toast}
      />
      <Toast
        isOpen={showErrorToast}
        icon={{ Component: ErrorIcon }}
        message={<FormattedMessage defaultMessage="Unable to submit rating" />}
        position="alternative"
        type="error"
        testId="difficulty-error-toast"
        className={styles.toast}
      />
    </>
  );
};

export default Difficulty;
